import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ElementRef, importProvidersFrom, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { AppClientDataService, ClientsStateModule } from '@mri-platform/import-export/clients-state';
import { CommonStateModule } from '@mri-platform/import-export/common-state';
import { CommonUiModule } from '@mri-platform/import-export/common-ui';
import { ieerrorEventChannelProvider, TokenErrorHandlerProvider } from '@mri-platform/import-export/core';
import { JobStateModule } from '@mri-platform/import-export/jobs-state';
import { MappersStateModule } from '@mri-platform/import-export/mappers-state';
import { IEShellModule } from '@mri-platform/import-export/shell';
import { UserEnrollmentConfigurationModule } from '@mri-platform/import-export/user-enrollment-configuration';
import {
  AppUserPermissionService,
  ImpersonateClientSwitchService,
  provideAppCoreIntegration
} from '@mri-platform/shared/app-core-integration';
import { provideAppInsightsIntegration } from '@mri-platform/shared/app-insights-integration';
import { provideAppNgrxIntegration } from '@mri-platform/shared/app-ngrx-data-integration';
import { provideNavigationState, provideViewState } from '@mri-platform/shared/app-ngrx-integration';
import {
  ClientDataService,
  ClientSwitchService,
  environment,
  FAKE_DB_COLLECTIONS_TOKEN,
  provideSharedCore,
  withInMemoryApi
} from '@mri-platform/shared/core';
import { UserPermissionService } from '@mri/angular-wfe-proxy-oidc';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, RouterState } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { NOTIFICATION_CONTAINER } from '@progress/kendo-angular-notification';
import { routes } from './app.routes';
import { AppComponent } from './containers/app.component';
import { isAuthzAppUserPredicateProvider } from './integration';
import { ROOT_REDUCERS } from './reducers';
import { devDb } from './testing/dev-dataset';
import { testDb } from './testing/test-dataset';

const sharedCoreFeatures = [environment.inMemoryApiEnabled ? withInMemoryApi() : null];
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    RouterModule.forRoot(routes, {
      initialNavigation: 'enabledBlocking',
      preloadingStrategy: PreloadAllModules
    }),
    IEShellModule,
    UserEnrollmentConfigurationModule,
    BrowserAnimationsModule,
    CommonUiModule
  ],
  providers: [
    provideStore(ROOT_REDUCERS, {
      metaReducers: !environment.production ? [] : [],
      runtimeChecks: {
        strictActionImmutability: false,
        strictStateImmutability: true,
        strictStateSerializability: true
      }
    }),
    provideEffects([]),
    !environment.production ? provideStoreDevtools({ connectInZone: true }) : [],
    provideRouterStore({
      stateKey: 'router',
      routerState: RouterState.Minimal
    }),
    provideAppInsightsIntegration(),
    provideAppCoreIntegration(),
    provideAppNgrxIntegration(),
    provideNavigationState(),
    provideViewState(),
    importProvidersFrom(CommonStateModule, MappersStateModule, JobStateModule, ClientsStateModule),
    provideHttpClient(withInterceptorsFromDi()),
    provideSharedCore({ useFeatureFlags: false }, ...sharedCoreFeatures.filter(f => f != null)),
    TokenErrorHandlerProvider,
    ieerrorEventChannelProvider,
    {
      //https://www.telerik.com/kendo-angular-ui/components/notification/api/NOTIFICATION_CONTAINER/
      provide: NOTIFICATION_CONTAINER,
      useFactory: () => ({ nativeElement: document.body }) as ElementRef
    },
    { provide: UserPermissionService, useClass: AppUserPermissionService },
    { provide: ClientDataService, useClass: AppClientDataService },
    { provide: ClientSwitchService, useClass: ImpersonateClientSwitchService },
    isAuthzAppUserPredicateProvider,
    {
      provide: FAKE_DB_COLLECTIONS_TOKEN,
      multi: true,
      useValue: environment.dev ? devDb : environment.integrationTest ? testDb : {}
    }
  ]
})
export class AppModule {}
